import { useNavigate, useSearch } from "@tanstack/react-router"
import { Button } from "design-system/atoms/Button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "design-system/molecules/DropdownMenu"
import { Code, Plus, Webhook, Wrench } from "lucide-react"
import { useTranslation } from "react-i18next"
import { CapabilityIdentifier } from "schema"

type CapabilityType = {
  name: string
  description: string
  icon: typeof Wrench | typeof Webhook | typeof Code
  value: string
  kind: "webhook" | "instruction" | "mcp"
}

export function CustomCapabilityButton() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const search = useSearch({ from: "/_base/_auth/capabilities/list" })

  const capabilityTypes: Array<CapabilityType> = [
    {
      name: t("capabilities.custom.instruction.name"),
      description: t("capabilities.custom.instruction.description"),
      icon: Wrench,
      value: CapabilityIdentifier.Instruction,
      kind: "instruction",
    },
    {
      name: t("capabilities.custom.webhook.name"),
      description: t("capabilities.custom.webhook.description"),
      icon: Webhook,
      value: CapabilityIdentifier.Webhook,
      kind: "webhook",
    },
    {
      name: t("capabilities.custom.mcp.name"),
      description: t("capabilities.custom.mcp.description"),
      icon: Code,
      value: CapabilityIdentifier.MCP,
      kind: "mcp",
    },
  ]

  const handleItemClick = (kind: "webhook" | "instruction" | "mcp") => {
    navigate({
      to: "/capabilities/list/create-custom",
      search: { ...search, kind },
    })
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button>
          <Plus className="size-4" />
          {t("capabilities.list.add")}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-full max-w-80">
        {capabilityTypes.map((item) => {
          const Icon = item.icon

          function handleClick() {
            handleItemClick(item.kind)
          }

          return (
            <DropdownMenuItem
              key={item.value}
              onClick={handleClick}
              className="flex flex-col items-start gap-1 px-3 py-2.5 cursor-pointer"
            >
              <div className="flex flex-row gap-1.5 justify-between items-center flex-1 w-full">
                <div className="flex items-center gap-1.5">
                  <div className="p-2 rounded-lg bg-muted shrink-0">
                    <Icon className="size-4 text-primary" />
                  </div>
                  <span className="font-medium text-base">{item.name}</span>
                </div>
              </div>
              <div className="flex flex-col gap-1 min-w-0 flex-1">
                <span className="text-xs text-muted-foreground leading-relaxed">
                  {item.description}
                </span>
              </div>
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
